<template>
  <div class="page-range-input" :class="{ 'has-error': errorMessage && isDirty }">
    <input
      class="page-range-input__input"
      type="text"
      :placeholder="$t('form-range-input.placeholder')"
      v-model="value"
      :name="name"
      :id="name"
      maxlength="100"
      @input="onInput"
    />
    <p v-if="errorMessage && isDirty" class="page-range-input__error">{{ errorMessage }}</p>
  </div>
</template>

<script>
import { debounce } from '@/directives/debounce';

// Regex pattern to allow only positive numbers greater than zero and valid ranges with optional spaces
const VALIDATION_PATTERN = /^([1-9]\d*)(\s*-\s*[1-9]\d*|[1-9]\d*)?(,\s*[1-9]\d*(\s*-\s*[1-9]\d*|[1-9]\d*)?)*$/;
const MAX_SET_SIZE = 10000;
const DEBOUNCE_MS = 600;

export default {
  name: 'PageRangeInput',
  data() {
    return {
      value: '',
      isDirty: false,
      errorMessage: '',
    };
  },
  props: {
    name: String,
    maxPage: {
      type: Number,
      default: Infinity,
    },
  },
  computed: {
    isValid() {
      return this.isValidFormat && this.isValidRange && this.isValidPageNumbers;
    },
    isValidFormat() {
      return VALIDATION_PATTERN.test(this.value);
    },
    isValidRange() {
      // Check if each range starts with a smaller number and ends with an equal or greater number
      return this.pageRanges.every((range) => range.length === 1 || range[0] <= range[1]);
    },
    isValidPageNumbers() {
      // Check if all page numbers are less than or equal to the maxPage
      return this.pageRanges.every((range) => range.every((page) => page <= this.maxPage));
    },
    pageRanges() {
      return this.value.split(',').map((range) => range.trim().split('-').map(Number));
    },
    selectedPages() {
      if (!this.isValid) return [];

      const selectedPages = new Set();
      for (let i = 0; i < this.pageRanges.length; i++) {
        // If the set reaches the maximum size, stop adding more pages
        if (selectedPages.size >= MAX_SET_SIZE) break;

        // Split the range by dash and convert to numbers
        const pages = this.pageRanges[i];

        // If the range has only one number, e.g., "8", add it to the Set
        if (pages.length === 1 && pages[0] <= this.maxPage) {
          selectedPages.add(pages[0]);
        } else {
          // If the range has two numbers, e.g., "1-5", add all numbers in the range to the Set
          for (let j = pages[0]; j <= pages[1]; j++) {
            // If the set reaches the maximum size, stop adding more pages
            if (selectedPages.size >= MAX_SET_SIZE) break;

            if (j <= this.maxPage) selectedPages.add(j);
          }
        }
      }

      // Convert Set to Array and sort it
      return Array.from(selectedPages).sort((a, b) => a - b);
    },
  },
  watch: {
    value: debounce(function () {
      if (this.isValid || !this.isDirty) {
        this.errorMessage = '';
      } else if (!this.isValidFormat || !this.isValidRange) {
        this.errorMessage = this.$t('form-range-input.invalid-page-range');
      } else if (!this.isValidPageNumbers) {
        this.errorMessage = this.$t('form-range-input.out-of-bounds-page-reference', { maxPage: this.maxPage });
      }
    }, DEBOUNCE_MS),
  },
  methods: {
    onInput() {
      // Set dirty state on input
      this.isDirty = true;

      // Debounced method for computing selectedPages
      this.updateSelectedPages();
    },
    updateSelectedPages: debounce(function () {
      this.$emit('update:selectedPages', this.selectedPages);
    }, DEBOUNCE_MS),
  },
};
</script>

<style lang="scss">
.page-range-input {
  padding: 24px 0;
  position: relative;
  width: 250px;
  max-width: 250px;

  &__input {
    background: $color-neutral-pale;
    border: 1px solid $color-neutral-pale;
    border-radius: $border-radius-small;
    color: $color-normal;
    font-size: 16px !important;
    min-height: 42px;
    outline: none;
    padding: 0 10px;
    transition: background 200ms ease, border 200ms ease;
    width: 100%;

    &:hover {
      border-color: $color-normal-pale;
    }

    &:focus {
      border-color: $color-accent;
    }
  }

  &__error {
    color: $color-error;
    font-family: Lato;
    font-size: 12px;
    font-weight: normal;
    font-stretch: normal;
    font-style: italic;
    line-height: 1.5;
    letter-spacing: normal;
  }

  &.has-error .page-range-input__input {
    border-color: $color-error;
  }

  &.is-dirty .page-range-input__input {
    background: lighten($color-neutral-pale, 10%); // Adjust as needed
  }
}
</style>
